import { FC, ReactNode } from 'react';
import Confetti from 'react-confetti';
import { useTranslation } from 'react-i18next';
import { ErrorIcon, WarningIcon } from '../../assets/icons';
import noConnectionImage from '../../assets/images/noConnectionImage.png';
import paymentSuccessImage from '../../assets/images/payment_success.png';
import purchaseSuccess from '../../assets/images/purchaseSuccess.png';
import SuccessImage from '../../assets/images/success.png';
import thankYouImage from '../../assets/images/thank_you_image.png';
import { Skeleton } from '../../components/Skeleton';
import MessageTemplate, {
  MessageProductTemplateProp,
} from '../../components/ProductTemplates/components/MessageTemplate';
import { ViewStateType } from '../../components/ProductTemplates/types';

export type MessageType = Pick<
  MessageProductTemplateProp,
  'title' | 'subTitle' | 'onClick' | 'buttonText' | 'content' | 'footer' | 'translationKey'
>;
enum DisplayTextKeys {
  TITLE = 'title',
  SUBTITLE = 'subTitle',
  BUTTON_TEXT = 'buttonText',
}

interface FeedbackTemplateProps {
  onClose?: () => void;
  templateConfig?: MessageType;
  children?: ReactNode;
  viewState: ViewStateType;
  onCTA?: (viewState: ViewStateType) => void;
}

export const FeedbackTemplate: FC<FeedbackTemplateProps> = ({
  onClose,
  templateConfig,
  children,
  viewState,
  onCTA,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {viewState === ViewStateType.THANK_YOU && (
        <MessageTemplate
          displayTextKeys={DisplayTextKeys}
          onClick={() => {
            if (onClose) onClose();
          }}
          translationKey="common:thankYou"
          imageSrc={thankYouImage}
          {...(templateConfig || {})}
        />
      )}

      {viewState === ViewStateType.CAMPAIGN_PURCHASE && (
        <>
          <Confetti className="w-full h-[250px]" />
          <MessageTemplate
            displayTextKeys={DisplayTextKeys}
            onClick={() => {
              if (onCTA) onCTA(ViewStateType.DEFAULT);
            }}
            translationKey="common:campaignPurchase"
            imageSrc={purchaseSuccess}
          />
        </>
      )}

      {viewState === ViewStateType.SELL_HOLDINGS && (
        <MessageTemplate
          displayTextKeys={DisplayTextKeys}
          onClick={() => {
            if (onClose) onClose();
          }}
          translationKey="common:sellHoldings"
          imageSrc={thankYouImage}
        />
      )}

      {viewState === ViewStateType.SOMETHING_WENT_WRONG && (
        <MessageTemplate
          onClick={() => {
            if (onCTA) onCTA(ViewStateType.DEFAULT);
          }}
          displayTextKeys={DisplayTextKeys}
          translationKey="common:somethingWentWrong"
          imageSrc={noConnectionImage}
          title={
            <div className="flex items-center gap-3">
              <WarningIcon className="text-yellow-600 dark:text-yellow-400" />
              <span className="text-20 font-medium text-gray-800 dark:text-gray-200">
                {t('common:somethingWentWrong.title')}
              </span>
            </div>
          }
        />
      )}
      {viewState === ViewStateType.ERROR_RESULT && (
        <MessageTemplate
          onClick={() => {
            if (onCTA) onCTA(ViewStateType.DEFAULT);
          }}
          displayTextKeys={DisplayTextKeys}
          translationKey="common:errorResult"
          title={
            <div className="flex flex-col">
              <ErrorIcon className="text-red-500 dark:text-red-400" />
              <span className="text-20 font-medium mt-5 text-gray-800 dark:text-gray-200">
                {t('common:errorResult.title')}
              </span>
            </div>
          }
          subTitle={
            <div className="text-14 mt-2 text-gray-600 dark:text-gray-400">{t('common:errorResult.subTitle')}</div>
          }
          {...templateConfig}
        />
      )}

      {viewState === ViewStateType.ACCEPT_BID_ERROR && (
        <MessageTemplate
          onClick={() => {
            if (onCTA) onCTA(ViewStateType.DEFAULT);
          }}
          displayTextKeys={DisplayTextKeys}
          translationKey="common:acceptBidError"
          title={
            <div className="flex flex-col justify-center items-center">
              <ErrorIcon className="text-red-500 dark:text-red-400" />
              <span className="text-20 font-medium mt-5 text-gray-800 dark:text-gray-200">
                {t('common:acceptBidError.title')}
              </span>
            </div>
          }
          subTitle={
            <div className="text-14 mt-2 text-gray-600 dark:text-gray-400">{t('common:acceptBidError.subTitle')}</div>
          }
          {...templateConfig}
        />
      )}

      {viewState === ViewStateType.SUCCESS && (
        <MessageTemplate
          onClick={() => {
            if (onClose) onClose();
          }}
          displayTextKeys={DisplayTextKeys}
          translationKey="common:generalSuccess"
          imageSrc={SuccessImage}
          {...(templateConfig || {})}
        />
      )}

      {viewState === ViewStateType.PAYMENT_SUCCESS && (
        <MessageTemplate
          onClick={() => {
            if (onCTA) onCTA(ViewStateType.DEFAULT);
          }}
          displayTextKeys={DisplayTextKeys}
          translationKey="common:paymentSuccess"
          imageSrc={paymentSuccessImage}
          {...(templateConfig || {})}
        />
      )}

      {viewState === ViewStateType.TOP_UP_SUCCESS && (
        <MessageTemplate
          onClick={() => {
            if (onCTA) onCTA(ViewStateType.DEFAULT);
          }}
          displayTextKeys={DisplayTextKeys}
          translationKey="common:topUpSuccess"
          imageSrc={paymentSuccessImage}
          {...(templateConfig || {})}
        />
      )}

      {viewState === ViewStateType.CUSTOM && <MessageTemplate displayTextKeys={DisplayTextKeys} {...templateConfig} />}

      {viewState === ViewStateType.MESSAGE && (
        <MessageTemplate
          displayTextKeys={DisplayTextKeys}
          translationKey="common:somethingWentWrong"
          imageSrc={noConnectionImage}
          {...templateConfig}
        />
      )}

      {viewState === ViewStateType.LOADING && (
        <MessageTemplate
          displayTextKeys={DisplayTextKeys}
          translationKey="common:loading"
          showButton={false}
          imageSrc={noConnectionImage}
          title={
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-5 rounded-full" />
              <span className="text-20 font-medium text-gray-800 dark:text-gray-200">{t('common:loading.title')}</span>
            </div>
          }
        />
      )}

      {viewState === ViewStateType.TOP_UP_LOADING && (
        <MessageTemplate
          displayTextKeys={DisplayTextKeys}
          translationKey="common:topupLoading"
          showButton={false}
          imageSrc={noConnectionImage}
          title={
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-5 rounded-full" />
              <span className="text-20 font-medium text-gray-800 dark:text-gray-200">{t('common:loading.title')}</span>
            </div>
          }
        />
      )}

      {viewState === ViewStateType.DEFAULT && (children || null)}
    </>
  );
};
